import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { buildSharedRecordTabsFromRecords } from 'components/patient/buildSharedRecordTabs';
import Tooltip from 'components/shared/Tooltip';
import LocationEpisode from 'models/LocationEpisode';
import Profile from 'models/Profile';
import { useSharedRecordTabsActions } from 'stores/sharedRecordTabsStore';
import { colors } from 'styles/theme/colors';
import LinkIcon from 'svg/LinkIcon';

import DraggablePatientCard from './PortfolioCard';
import SharedRecordSelectionModal from './SharedRecordSelectionModal';

type StackedPatientCardsProps = {
  cards: LocationEpisode[];
  profile: Profile;
  invalidateColumn: () => void;
};

function SharedRecordIndicator() {
  return (
    <Tooltip text='Patient has shared records in Olio' position='left'>
      <LinkIndicator>
        <LinkIcon width={8} height={8} color={colors.black50} />
      </LinkIndicator>
    </Tooltip>
  );
}

const CARD_GAP = 10;
const COLLAPSED_OFFSET = 6;
const COLLAPSED_SCALE_STEP = 0.03;
const COLLAPSED_OPACITY_STEP = 0.2;

function useCardHeights() {
  const [heights, setHeights] = useState<number[]>([]);
  const observersRef = useRef<Map<number, ResizeObserver>>(new Map());

  const measureRef = useCallback(
    (index: number) => (node: HTMLDivElement | null) => {
      const existing = observersRef.current.get(index);
      if (existing) {
        existing.disconnect();
        observersRef.current.delete(index);
      }

      if (!node) return;

      const observer = new ResizeObserver(([entry]) => {
        const height = entry.borderBoxSize?.[0]?.blockSize ?? entry.contentRect.height;
        setHeights((prev) => {
          const next = [...prev];
          next[index] = height;
          return next;
        });
      });

      observer.observe(node);
      observersRef.current.set(index, observer);
    },
    []
  );

  useEffect(() => {
    const observers = observersRef.current;
    return () => {
      observers.forEach((o) => o.disconnect());
      observers.clear();
    };
  }, []);

  const cumulativeOffsets = heights.reduce<number[]>((acc, h, i) => {
    const prev = i === 0 ? 0 : acc[i - 1];
    acc.push(prev + (h || 0) + CARD_GAP);
    return acc;
  }, []);

  const primaryHeight = heights[0] ?? 0;
  const lastStackedIndex = heights.length - 1;
  const lastStackedOffset = lastStackedIndex > 0 ? (cumulativeOffsets[lastStackedIndex - 1] ?? 0) : 0;
  const lastStackedHeight = lastStackedIndex > 0 ? (heights[lastStackedIndex] ?? 0) : 0;
  const lineHeight = lastStackedOffset + lastStackedHeight / 2 - primaryHeight / 2;

  const expandedPadding = lastStackedOffset + lastStackedHeight - primaryHeight;

  return {
    measureRef,
    heights,
    cumulativeOffsets,
    expandedPadding,
    primaryHeight,
    lineHeight,
    stackedHeights: heights.slice(1),
  };
}

export default function StackedPatientCards({ cards, profile, invalidateColumn }: StackedPatientCardsProps) {
  const [isHovered, setIsHovered] = useState(false);
  const [modalRecords, setModalRecords] = useState<LocationEpisode[] | null>(null);
  const navigate = useNavigate();
  const { setTabs } = useSharedRecordTabsActions();

  const allRecords = [cards[0], ...cards[0].sharedRecords];
  const hasHiddenRecords = allRecords.length > cards.length;

  const cardClick = (le: LocationEpisode) =>
    hasHiddenRecords
      ? () => setModalRecords(allRecords)
      : () => {
          setTabs(buildSharedRecordTabsFromRecords(allRecords), le.id);
          navigate(`/patients/${le.episodeId}?locationEpisodeId=${le.id}`);
        };

  const stackedCards = cards.length > 1 ? cards.slice(1) : [];
  const { measureRef, cumulativeOffsets, expandedPadding, primaryHeight, lineHeight, stackedHeights } =
    useCardHeights();

  if (cards.length === 1) {
    const le = cards[0];
    const hasSharedRecords = le.sharedRecords.length > 0;

    return (
      <>
        {hasSharedRecords ? (
          <SingleLinkedCard>
            <SharedRecordIndicator />
            <DraggablePatientCard
              locationEpisode={le}
              profile={profile}
              invalidateColumn={invalidateColumn}
              onCardClick={cardClick(le)}
            />
          </SingleLinkedCard>
        ) : (
          <DraggablePatientCard locationEpisode={le} profile={profile} invalidateColumn={invalidateColumn} />
        )}
        {modalRecords && (
          <SharedRecordSelectionModal records={modalRecords} profile={profile} onClose={() => setModalRecords(null)} />
        )}
      </>
    );
  }

  return (
    <>
      <StackContainer
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        $expanded={isHovered}
        $expandedPadding={isHovered ? expandedPadding : stackedCards.length * COLLAPSED_OFFSET}
        $lineHeight={lineHeight}
        $primaryHeight={primaryHeight}>
        {stackedCards.map((card, i) => (
          <StackedLayer
            ref={measureRef(i + 1)}
            key={card.id}
            $index={i}
            $total={stackedCards.length}
            $expanded={isHovered}
            $expandOffset={cumulativeOffsets[i] ?? 0}
            $primaryHeight={primaryHeight}
            $ownHeight={stackedHeights[i] ?? 0}>
            <SharedRecordIndicator />
            <DraggablePatientCard
              locationEpisode={card}
              profile={profile}
              invalidateColumn={invalidateColumn}
              onCardClick={cardClick(card)}
            />
          </StackedLayer>
        ))}

        <PrimaryLayer ref={measureRef(0)} $stackCount={stackedCards.length}>
          <SharedRecordIndicator />
          <DraggablePatientCard
            locationEpisode={cards[0]}
            profile={profile}
            invalidateColumn={invalidateColumn}
            onCardClick={cardClick(cards[0])}
          />
        </PrimaryLayer>
      </StackContainer>
      {modalRecords && (
        <SharedRecordSelectionModal records={modalRecords} profile={profile} onClose={() => setModalRecords(null)} />
      )}
    </>
  );
}

const StackContainer = styled.div<{
  $expandedPadding: number;
  $expanded: boolean;
  $lineHeight: number;
  $primaryHeight: number;
}>`
  position: relative;
  isolation: isolate;
  cursor: pointer;
  padding-bottom: ${({ $expandedPadding }) => $expandedPadding}px;
  transition: padding-bottom 300ms ease-out;

  &::before {
    content: '';
    position: absolute;
    left: -6px;
    top: ${({ $primaryHeight }) => $primaryHeight / 2}px;
    height: ${({ $expanded, $lineHeight }) => ($expanded ? `${$lineHeight}px` : '0px')};
    width: 0;
    border-left: 2px dotted var(--black-25);
    z-index: 0;
    transition: height 300ms ease-out;
  }
`;

const PrimaryLayer = styled.div<{ $stackCount: number }>`
  position: relative;
  z-index: ${({ $stackCount }) => $stackCount + 1};
`;

const StackedLayer = styled.div<{
  $index: number;
  $total: number;
  $expanded: boolean;
  $expandOffset: number;
  $primaryHeight: number;
  $ownHeight: number;
}>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: ${({ $index, $total }) => $total - $index};
  transition: all 300ms ease-out;
  transform-origin: bottom center;
  transform: ${({ $expanded, $index, $expandOffset, $primaryHeight, $ownHeight }) => {
    if ($expanded) return `translateY(${$expandOffset}px)`;
    const isTaller = $ownHeight > $primaryHeight && $primaryHeight > 0;
    const scaleY = isTaller ? $primaryHeight / $ownHeight : 1;
    const bottomAlignY = $primaryHeight + ($index + 1) * COLLAPSED_OFFSET - $ownHeight;
    return `translateY(${bottomAlignY}px) scaleX(${1 - ($index + 1) * COLLAPSED_SCALE_STEP}) scaleY(${scaleY})`;
  }};
  opacity: ${({ $expanded, $index }) => ($expanded ? 1 : 1 - ($index + 1) * COLLAPSED_OPACITY_STEP)};
`;

const SingleLinkedCard = styled.div`
  position: relative;
  isolation: isolate;
`;

const LinkIndicator = styled.div`
  position: absolute;
  left: -10px;
  top: 50%;
  transform: translateY(-50%);
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: ${colors.white};
  border: 1px solid ${colors.black10};
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
`;
